import { FC, ReactElement, useState } from "react";
import { CartesianGrid, DotItemDotProps, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { ProjectRunPerformanceResult } from "@app/operations/project-performance/types";

export interface TotalRuntimeChartProps {
  data: ProjectRunPerformanceResult[];
}

interface RuntimeDataPoint {
  datePublished: string;
  projectRunConfigurationId: number;
  totalWallClockMinutes: number;
}

interface HoveredDot {
  point: RuntimeDataPoint;
  x: number;
  y: number;
}

function formatRunDate(value: string): string {
  return new Date(value).toLocaleDateString("en-US", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function toDataPoints(data: ProjectRunPerformanceResult[]): RuntimeDataPoint[] {
  return data
    .filter(run => !!run.datePublished)
    .map(run => ({
      datePublished: run.datePublished as string,
      projectRunConfigurationId: run.projectRunConfigurationId,
      totalWallClockMinutes: run.totalWallClockMinutes ?? 0,
    }));
}

export const TotalRuntimeChart: FC<TotalRuntimeChartProps> = ({ data }) => {
  const dataPoints = toDataPoints(data);
  const [hoveredDot, setHoveredDot] = useState<HoveredDot | undefined>();

  if (dataPoints.length === 0) {
    return <p data-testid="total-runtime-chart__empty">No performance data available for this project.</p>;
  }

  const renderDot = (props: DotItemDotProps): ReactElement => {
    const { cx, cy } = props;
    const payload = props.payload as RuntimeDataPoint | undefined;
    if (cx == null || cy == null || !payload) {
      return <></>;
    }

    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill="#056edf"
        stroke="#ffffff"
        strokeWidth={1}
        data-testid={`total-runtime-chart__dot-${payload.projectRunConfigurationId}`}
        onMouseEnter={() => setHoveredDot({ point: payload, x: cx, y: cy })}
        onMouseLeave={() => setHoveredDot(undefined)}
      />
    );
  };

  return (
    <div className="total-runtime-chart" style={{ position: "relative" }}>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={dataPoints} margin={{ bottom: 20, left: 20, right: 30, top: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="datePublished" tickFormatter={formatRunDate} />
          <YAxis label={{ angle: -90, position: "left", style: { textAnchor: "middle" }, value: "Minutes" }} />
          <Line type="monotone" dataKey="totalWallClockMinutes" stroke="#056edf" dot={renderDot} activeDot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
      {hoveredDot && (
        <div
          className="total-runtime-chart__tooltip bg-white border rounded p-2"
          data-testid="total-runtime-chart__tooltip"
          style={{ left: hoveredDot.x + 10, pointerEvents: "none", position: "absolute", top: hoveredDot.y - 10 }}
        >
          <div>Run Configuration: {hoveredDot.point.projectRunConfigurationId}</div>
          <div>Date: {formatRunDate(hoveredDot.point.datePublished)}</div>
          <div>Minutes: {hoveredDot.point.totalWallClockMinutes.toLocaleString()}</div>
        </div>
      )}
    </div>
  );
};

export default TotalRuntimeChart;
